import React, { useState, useEffect } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { Box, Typography, Button, Stack, Chip, IconButton, Alert, Collapse, Grid, TextField } from '@mui/material';
import { CloudUpload, CheckCircle, Delete, Description, PhotoCamera, Warning, ZoomIn, Visibility } from '@mui/icons-material';
import { getTemplate, parseFileArray } from '../../utils/stepTemplates';
import { getAdmFileUrl } from '../../utils/admProgress';
import { isFisikDefaultRtlh, isFisikManualRehab, isFisikPembangunan } from '../../utils/fisikProgress';
import SecureFilePdf from '../SecureFilePdf';
import { toProtectedFileUrl } from '../../utils/secureFile';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';
const MAX_FILE_SIZE = 2 * 1024 * 1024;

const formatRpLocal = (value) => {
  if (!value) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const PDF_ONLY_LABELS = ['Proposal', 'Surat Pernyataan', 'RAB', 'Gambar Teknis', 'Rekap Volume', 'BAP (Berita Acara Pembayaran)', 'SPM', 'Kwitansi Pembayaran ke Penerima'];
const IMAGE_OR_PDF_LABELS = ['KTP', 'KK'];
const REHAB_JENIS = /rehab/i;

function isPdfOnlyLabel(label) {
  if (!label) return false;
  const lower = String(label).toLowerCase().trim();
  return PDF_ONLY_LABELS.some((p) => lower === p.toLowerCase()) || lower.startsWith('bukti tf dari rekening penerima ke supplier');
}

function getFileTypeHint(label) {
  if (!label) return null;
  const lower = String(label).toLowerCase().trim();
  if (IMAGE_OR_PDF_LABELS.some((p) => lower === p.toLowerCase())) return 'Gambar atau PDF';
  if (isPdfOnlyLabel(label)) return 'Wajib PDF';
  return 'PDF atau Gambar';
}

function validateFile(file, isFoto, pdfOnly) {
  if (!file) return null;
  if (file.size > MAX_FILE_SIZE) {
    return `File "${file.name}" melebihi 2MB (${(file.size / 1024 / 1024).toFixed(1)}MB)`;
  }
  const isPdfFile = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  const isImageFile = file.type.startsWith('image/') || /\.(jpe?g|png)$/i.test(file.name);
  if (pdfOnly) {
    if (!isPdfFile) return 'File harus berformat PDF';
    return null;
  }
  if (isFoto) {
    if (!isImageFile) return 'File harus berupa gambar (jpg/png)';
  } else {
    if (!isPdfFile && !isImageFile) return 'File harus PDF atau gambar (jpg/png)';
  }
  return null;
}

const FileSlot = ({ label, existingFile, pendingFile, fileBase, onChange, onDelete, errorMessage, pdfOnly, previewUrl, previewType }) => {
  const acceptAttr = pdfOnly ? '.pdf,application/pdf' : '.pdf,.jpg,.jpeg,.png,image/jpeg,image/png';
  const typeHint = getFileTypeHint(label.replace(/^\d+\.\s*/, ''));

  // Jika ada file baru (pending), preview blob-nya menang atas file existing yang diganti.
  const effectiveUrl = pendingFile
    ? (previewUrl || null)
    : (existingFile ? toProtectedFileUrl(getAdmFileUrl(existingFile, fileBase, BASE_URL)) : null);
  const effectiveType = pendingFile
    ? (previewType || pendingFile.type || (/\.pdf$/i.test(pendingFile.name) ? 'application/pdf' : null))
    : (existingFile && /\.pdf$/i.test(existingFile) ? 'application/pdf' : null);
  const isPdf = effectiveType === 'application/pdf' || (!effectiveType && /\.pdf$/i.test(pendingFile?.name || existingFile || ''));
  const isImage = (effectiveType || '').startsWith('image/') || (!effectiveType && /\.(jpg|jpeg|png|gif|webp)$/i.test(pendingFile?.name || existingFile || ''));
  const canPreview = !!effectiveUrl;
  const displayName = pendingFile?.name || existingFile;

  return (
  <Box sx={{ mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1.5, bgcolor: '#fafafa' }}>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
      {label}
      {typeHint && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({typeHint})</Typography>}
    </Typography>
    {errorMessage && (
      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
        <Warning sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />{errorMessage}
      </Typography>
    )}
    {canPreview && isImage && (
      <Box sx={{ mb: 1, textAlign: 'center' }}>
        <img src={effectiveUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }} onClick={() => openInAppFilePreview({ url: effectiveUrl })} />
      </Box>
    )}
    {canPreview && isPdf && (
      <Box sx={{ mb: 1, textAlign: 'center' }}>
        <SecureFilePdf src={effectiveUrl} title={label} height={220} />
        <Button size="small" startIcon={<ZoomIn />} onClick={() => openInAppFilePreview({ url: effectiveUrl })} sx={{ fontSize: 11, mt: 0.5 }}>Buka PDF</Button>
      </Box>
    )}
    {displayName ? (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />} label={pendingFile ? `${displayName} (baru)` : displayName} variant="outlined" color="success"
          onClick={() => effectiveUrl && openInAppFilePreview({ url: effectiveUrl })}
          sx={{ cursor: 'pointer', maxWidth: 260 }} />
        <IconButton size="small" onClick={onDelete}><Delete fontSize="small" /></IconButton>
        <Button component="label" size="small" variant="text" sx={{ fontSize: 11 }}>
          Ganti
          <input type="file" hidden accept={acceptAttr} onChange={onChange} />
        </Button>
      </Stack>
    ) : (
      <Button component="label" variant="outlined" size="small" startIcon={<CloudUpload />} sx={{ fontSize: 11 }}>
        Upload
        <input type="file" hidden accept={acceptAttr} onChange={onChange} />
      </Button>
    )}
  </Box>
  );
};

const ImageSlot = ({ label, existingFile, pendingFile, fileBase, onChange, onDelete, errorMessage, previewUrl }) => (
  <Box sx={{ mb: 1, p: 1, border: '1px solid #ffe0b2', borderRadius: 1.5, bgcolor: '#fff8e1' }}>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>{label}</Typography>
    {errorMessage && (
      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5, fontSize: 10 }}>
        <Warning sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />{errorMessage}
      </Typography>
    )}
    {(pendingFile ? previewUrl : (existingFile ? toProtectedFileUrl(getAdmFileUrl(existingFile, fileBase, BASE_URL)) : null)) && (
      <Box sx={{ mb: 1, textAlign: 'center' }}>
        <img src={pendingFile ? previewUrl : getAdmFileUrl(existingFile, fileBase, BASE_URL)} alt="preview" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }} onClick={() => openInAppFilePreview({ url: pendingFile ? previewUrl : getAdmFileUrl(existingFile, fileBase, BASE_URL) })} />
      </Box>
    )}
    {(pendingFile || existingFile) ? (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />} label={pendingFile ? pendingFile.name : 'Terupload'} color="success" sx={{ fontSize: 10, maxWidth: 220 }} />
        <IconButton size="small" onClick={onDelete}><Delete fontSize="small" /></IconButton>
        <Button component="label" size="small" variant="text" sx={{ fontSize: 11 }}>
          Ganti
          <input type="file" hidden accept="image/*" onChange={onChange} />
        </Button>
      </Stack>
    ) : (
      <Button component="label" variant="outlined" size="small" startIcon={<CloudUpload />} sx={{ fontSize: 11 }}>
        Upload
        <input type="file" hidden accept="image/*" onChange={onChange} />
      </Button>
    )}
  </Box>
);

export default function StepUploadForm({ step, type, onSave, onCancel, saving, jenisBantuan, saveButtonColor, supplierCount = 0, supplierNames = [], penerimaData = null, nominalMaterial = 0 }) {
  const [dokFiles, setDokFiles] = useState({});
  const [fotoFiles, setFotoFiles] = useState({});
  const [dokPreview, setDokPreview] = useState({});
  const [fotoPreview, setFotoPreview] = useState({});
  const [dokDeletes, setDokDeletes] = useState([]);
  const [fotoDeletes, setFotoDeletes] = useState([]);
  const [errors, setErrors] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [anggaranPenggunaan, setAnggaranPenggunaan] = useState(step.anggaranPenggunaan || '');
  const [anggaranLangkah, setAnggaranLangkah] = useState(step.anggaran || '');
  const [tahapLangkah, setTahapLangkah] = useState(step.tahap || '');
  const [catatanLangkah, setCatatanLangkah] = useState(step.catatan || '');
  const [signedBaFile, setSignedBaFile] = useState(null);
  const [signedBaError, setSignedBaError] = useState('');

  useEffect(() => {
    const previews = { ...dokPreview, ...fotoPreview };
    return () => {
      Object.values(previews).forEach(url => { if (url && url.startsWith('blob:')) URL.revokeObjectURL(url); });
    };
  }, [dokPreview, fotoPreview]);

  const template = getTemplate(type, step.urutan, jenisBantuan);
  if (!template) return <Typography variant="body2" color="text.secondary">Template tidak ditemukan.</Typography>;

  const existingDok = parseFileArray(step.dokumen);
  const existingFoto = parseFileArray(step.foto);

  const isRehab = REHAB_JENIS.test(jenisBantuan || '');
  const isFisikRtlh = type === 'fisik' && isFisikDefaultRtlh(jenisBantuan);
  const isFisikManual = type === 'fisik' && isFisikManualRehab(jenisBantuan);
  const isFisikPembangunanTemplate = type === 'fisik' && isFisikPembangunan(jenisBantuan);
  const acceptsAnggaranLangkah = isFisikManual || isFisikPembangunanTemplate;
  const isVerifikasiFaktual = type === 'administrasi' && step.urutan === 2;

  // Dokumen langkah Validasi Faktual & Survei dihasilkan sistem (Form Validasi & BA Survei otomatis),
  // sehingga tidak diwajibkan upload; user hanya upload Foto survei bersama.
  const dokumenSistemLabels = isVerifikasiFaktual ? (template.dokumenWajib || []) : [];

  const baseDokumenWajib = (template.dokumenWajib || []).filter((label) => {
    if (isRehab && String(label).toLowerCase() === 'gambar teknis') return false;
    if (isVerifikasiFaktual) return false; // dokumen diisi otomatis sistem
    return true;
  });

  const supplierTransferSlots = [];
  // Keuangan Pembayaran Supplier Tahap I (urutan 3) & II (urutan 5): bukti TF per masing-masing supplier
  if (type === 'keuangan' && (step.urutan === 3 || step.urutan === 5)) {
    const n = Math.max(1, supplierCount);
    for (let i = 0; i < n; i++) {
      const nama = supplierNames[i] ? ` (${supplierNames[i]})` : '';
      supplierTransferSlots.push(`Bukti Transfer ke Supplier ${i + 1}${nama}`);
    }
  }

  // Untuk keuangan Pembayaran Supplier, slot supplier menggantikan dokumen wajib default ('Bukti Transfer')
  const isKeuanganPerSupplier = type === 'keuangan' && (step.urutan === 3 || step.urutan === 5);
  const dokumenWajib = isKeuanganPerSupplier
    ? supplierTransferSlots
    : [...baseDokumenWajib, ...supplierTransferSlots];

  const handleDokChange = (idx, e) => {
    const f = e.target.files[0];
    if (!f) return;
    const pdfOnly = isPdfOnlyLabel(dokumenWajib[idx]);
    const err = validateFile(f, false, pdfOnly);
    if (err) {
      setErrors(p => ({ ...p, [`dok_${idx}`]: err }));
      e.target.value = '';
      return;
    }
    setErrors(p => { const n = { ...p }; delete n[`dok_${idx}`]; return n; });
    setDokFiles(p => ({ ...p, [idx]: f }));
    // file baru menggantikan slot ini -> batalkan tanda hapus slot
    setDokDeletes(p => p.filter((i) => i !== idx));
    setDokPreview(p => {
      if (p[idx]?.startsWith('blob:')) URL.revokeObjectURL(p[idx]);
      return { ...p, [idx]: URL.createObjectURL(f) };
    });
  };

  const handleDokDelete = (idx) => {
    if (dokPreview[idx]?.startsWith('blob:')) URL.revokeObjectURL(dokPreview[idx]);
    setDokFiles(p => { const n = { ...p }; delete n[idx]; return n; });
    setDokPreview(p => { const n = { ...p }; delete n[idx]; return n; });
    setDokDeletes(p => (p.includes(idx) ? p : [...p, idx]));
    setErrors(p => { const n = { ...p }; delete n[`dok_${idx}`]; return n; });
  };

  const handleFotoChange = (idx, e) => {
    const f = e.target.files[0];
    if (!f) return;
    const err = validateFile(f, true, false);
    if (err) {
      setErrors(p => ({ ...p, [`foto_${idx}`]: err }));
      e.target.value = '';
      return;
    }
    setErrors(p => { const n = { ...p }; delete n[`foto_${idx}`]; return n; });
    setFotoFiles(p => ({ ...p, [idx]: f }));
    // file baru menggantikan slot ini -> batalkan tanda hapus slot
    setFotoDeletes(p => p.filter((i) => i !== idx));
    setFotoPreview(p => {
      if (p[idx]?.startsWith('blob:')) URL.revokeObjectURL(p[idx]);
      return { ...p, [idx]: URL.createObjectURL(f) };
    });
  };

  const handleFotoDelete = (idx) => {
    if (fotoPreview[idx]?.startsWith('blob:')) URL.revokeObjectURL(fotoPreview[idx]);
    setFotoFiles(p => { const n = { ...p }; delete n[idx]; return n; });
    setFotoPreview(p => { const n = { ...p }; delete n[idx]; return n; });
    setFotoDeletes(p => (p.includes(idx) ? p : [...p, idx]));
    setErrors(p => { const n = { ...p }; delete n[`foto_${idx}`]; return n; });
  };

  const getMissingFiles = () => {
    const missing = [];
    dokumenWajib.forEach((label, idx) => {
      if (label.toLowerCase().includes('opsional')) return;
      const hasExisting = existingDok[idx] && !dokDeletes.includes(idx);
      const hasNew = !!dokFiles[idx];
      if (!hasExisting && !hasNew) missing.push(label);
    });
    (template.fotoWajib || []).forEach((label, idx) => {
      if (label.toLowerCase().includes('opsional')) return;
      const hasExisting = existingFoto[idx] && !fotoDeletes.includes(idx);
      const hasNew = !!fotoFiles[idx];
      if (!hasExisting && !hasNew) missing.push(`Foto ${label}`);
    });
    return missing;
  };

  const handleSave = () => {
    const missing = getMissingFiles();
    const hasErrors = Object.keys(errors).length > 0;

    if (missing.length > 0) {
      setShowWarning(true);
      return;
    }
    if (hasErrors) {
      return;
    }

    const formData = new FormData();
    const hasNewFoto = Object.keys(fotoFiles).length > 0;
    const hasAnggaran = Number(anggaranPenggunaan) > 0;
    const hasAnggaranLangkah = Number(anggaranLangkah) > 0;
    const nextStatus = (hasNewFoto || hasAnggaran || hasAnggaranLangkah) && step.status === 'Belum Dimulai' ? 'Sedang Berlangsung' : (step.status || 'Belum Dimulai');
    formData.append('status', nextStatus);

    // Rehab manual: auto-sync anggaranPenggunaan = anggaranLangkah
    if (isFisikManual && hasAnggaranLangkah) {
      formData.append('anggaranPenggunaan', String(Number(anggaranLangkah)));
    } else if (isFisikRtlh && hasAnggaran) {
      formData.append('anggaranPenggunaan', String(Number(anggaranPenggunaan)));
    }

    if (isFisikManual) {
      if (tahapLangkah.trim()) formData.append('tahap', tahapLangkah.trim());
      if (catatanLangkah.trim()) formData.append('catatan', catatanLangkah.trim());
    }

    if (acceptsAnggaranLangkah && hasAnggaranLangkah) {
      formData.append('anggaran', String(Number(anggaranLangkah)));
    }

    Object.entries(fotoFiles).forEach(([idx, file]) => {
      formData.append('foto_idx', String(idx));
      formData.append('foto', file);
    });
    existingFoto.forEach((f, i) => {
      if (!fotoDeletes.includes(i)) {
        formData.append('foto_existing', f);
      }
    });
    Object.entries(dokFiles).forEach(([idx, file]) => {
      formData.append('dokumen_idx', String(idx));
      formData.append('dokumen', file);
    });
    existingDok.forEach((d, i) => {
      if (!dokDeletes.includes(i)) {
        formData.append('dokumen_existing', d);
      }
    });
    if (signedBaFile) formData.append('ba_signed', signedBaFile);

    onSave(formData);
  };

  const hasDok = dokumenWajib.length > 0;
  const hasFoto = template.fotoWajib?.length > 0;
  if (!hasDok && !hasFoto) return <Typography variant="body2" color="text.secondary">Tidak ada berkas wajib di tahap ini.</Typography>;

  const missing = getMissingFiles();
  const allComplete = missing.length === 0;

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{template.tahap} ({template.bobot}%)</Typography>
      {type === 'administrasi' && step.urutan === 2 && penerimaData && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f7ff', borderRadius: 1.5, border: '1px solid #bbdefb' }}>
          <Typography variant="caption" fontWeight={600} color="primary" sx={{ display: 'block', mb: 1 }}>
            Data Penerima Bantuan (Sumber: {penerimaData.sumber || '-'})
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Nama</Typography><Typography variant="body2" fontWeight={500}>{penerimaData.nama || '-'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">NIK/KTP</Typography><Typography variant="body2">{penerimaData.nik || penerimaData.nomorKTP || '-'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">No. KK</Typography><Typography variant="body2">{penerimaData.nomorKK || '-'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Alamat</Typography><Typography variant="body2">{penerimaData.alamat || '-'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Kecamatan</Typography><Typography variant="body2">{penerimaData.kecamatan || '-'}</Typography></Grid>
            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Desa/Kelurahan</Typography><Typography variant="body2">{penerimaData.desaKelurahan || '-'}</Typography></Grid>
            {penerimaData.kategori && <Grid item xs={6}><Typography variant="caption" color="text.secondary">Kategori</Typography><Typography variant="body2">{penerimaData.kategori}</Typography></Grid>}
            {penerimaData.score != null && <Grid item xs={6}><Typography variant="caption" color="text.secondary">Score</Typography><Typography variant="body2">{penerimaData.score}</Typography></Grid>}
          </Grid>
        </Box>
      )}
      {isVerifikasiFaktual && dokumenSistemLabels.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Description fontSize="small" /> Dokumen Sistem (Otomatis)
          </Typography>
          {dokumenSistemLabels.map((label, idx) => {
            const isBA = /ba survei|berita acara/i.test(label);
            const file = isBA ? existingDok.find(f => String(f).startsWith('BA_Verifikasi_')) : null;
            return (
              <Box key={idx} sx={{ mb: 0.5, p: 1, border: '1px dashed #90caf9', borderRadius: 1.5, bgcolor: '#e3f2fd' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />}
                    label={isBA ? (file ? `BA Survei (${file})` : 'BA Survei (otomatis)') : 'Form Validasi (data sistem)'}
                    color="info" variant="outlined" />
                  {isBA && file && (
                    <Button size="small" variant="outlined" startIcon={<Visibility />}
                      onClick={() => openInAppFilePreview({ url: toProtectedFileUrl(getAdmFileUrl(file, null, BASE_URL)) })}>
                      Lihat BA
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })}
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption">Draft Berita Acara dibuat otomatis. Setelah ditandatangani, unggah ulang versi PDF bertanda tangan di bawah ini.</Typography>
          </Alert>
          <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid #a5d6a7', borderRadius: 1.5, bgcolor: '#f1f8e9' }}>
            <Typography variant="body2" fontWeight={600}>BA Ditandatangani (PDF)</Typography>
            {existingDok.find(f => String(f).startsWith('BA_Signed_')) && (
              <Button size="small" startIcon={<Visibility />} onClick={() => openInAppFilePreview({ url: toProtectedFileUrl(getAdmFileUrl(existingDok.find(f => String(f).startsWith('BA_Signed_')), 'rab-progress-administrasi', BASE_URL)) })}>Lihat BA Bertanda Tangan</Button>
            )}
            <Button component="label" size="small" variant="outlined" sx={{ mt: 1, display: 'flex', width: 'fit-content' }}>
              {signedBaFile ? 'Ganti File Terpilih' : 'Upload / Ganti BA Bertanda Tangan'}
              <input hidden type="file" accept="application/pdf,.pdf" onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (file && file.type !== 'application/pdf') { setSignedBaError('BA bertanda tangan wajib berupa PDF.'); setSignedBaFile(null); return; }
                setSignedBaError(''); setSignedBaFile(file);
              }} />
            </Button>
            {signedBaFile && <Typography variant="caption" display="block" sx={{ mt: .5 }}>{signedBaFile.name}</Typography>}
            {signedBaError && <Typography variant="caption" color="error">{signedBaError}</Typography>}
          </Box>
        </Box>
      )}
        {/* <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">Langkah ini terintegrasi otomatis dari dokumen Administrasi (Transfer Dana).</Typography>
        </Alert> */}
      
      {type === 'administrasi' && step.urutan === 4 && (
        <Box sx={{ mb: 2, p: 1.5, border: '1px dashed #90caf9', borderRadius: 1.5, bgcolor: '#e3f2fd' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Description fontSize="small" /> SK Penetapan Penerima Bantuan (Sistem)
          </Typography>
          {existingDok.find(f => String(f).startsWith('SK_')) ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip size="small" icon={<CheckCircle sx={{ fontSize: 14 }} />} color="info" variant="outlined"
                label={`SK ${jenisBantuan && /rehab/i.test(jenisBantuan) ? 'Rehabilitasi RTLH' : 'Pembangunan RLH'} — otomatis dari Data SK`} />
              <Button size="small" variant="outlined" startIcon={<Visibility />}
                onClick={() => openInAppFilePreview({ url: toProtectedFileUrl(getAdmFileUrl(existingDok.find(f => String(f).startsWith('SK_')), 'rab-progress-administrasi', BASE_URL)) })}>
                Lihat SK
              </Button>
            </Stack>
          ) : (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              <Typography variant="caption">SK Penetapan untuk jenis bantuan ini belum diupload Admin pada menu Data SK.</Typography>
            </Alert>
          )}
        </Box>
      )}

      {template.spjAutoFill && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">SPJ terisi otomatis dari semua dokumen administrasi yang telah diupload.</Typography>
        </Alert>
      )}
      {isRehab && type === 'administrasi' && step.urutan === 3 && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">Jenis bantuan Rehab: Gambar Teknis tidak wajib. Hanya RAB & Rekap Volume (PDF).</Typography>
        </Alert>
      )}

      <Collapse in={showWarning && missing.length > 0}>
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setShowWarning(false)}>
          <Typography variant="caption" fontWeight={600}>Berkas belum lengkap:</Typography>
          <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12 }}>
            {missing.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </Alert>
      </Collapse>

      {allComplete && (
        <Alert severity="success" sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="caption">Semua berkas lengkap. Simpan untuk menyelesaikan tahap ini.</Typography>
        </Alert>
      )}

      {hasDok && isKeuanganPerSupplier ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Description fontSize="small" /> Bukti Transfer ke Supplier ({supplierTransferSlots.length}) — Maks 2MB, wajib PDF
          </Typography>
          {supplierTransferSlots.length === 0 && (
            <Alert severity="warning" sx={{ py: 0.5, mb: 1 }}>
              <Typography variant="caption">Belum ada data supplier pada penerima bantuan ini. Tambahkan supplier terlebih dahulu.</Typography>
            </Alert>
          )}
          {supplierTransferSlots.map((label, idx) => {
            const existing = existingDok[idx] && !dokDeletes.includes(idx) ? existingDok[idx] : null;
            return <FileSlot key={idx} label={`${idx + 1}. ${label}`} existingFile={existing} pendingFile={dokFiles[idx]} errorMessage={errors[`dok_${idx}`]} pdfOnly previewUrl={dokPreview[idx]} previewType={dokFiles[idx]?.type} onChange={(e) => handleDokChange(idx, e)} onDelete={() => handleDokDelete(idx)} />;
          })}
        </Box>
      ) : hasDok && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Description fontSize="small" /> Dokumen Wajib ({dokumenWajib.length}) — Maks 2MB
          </Typography>
          {dokumenWajib.map((label, idx) => {
            const existing = existingDok[idx] && !dokDeletes.includes(idx) ? existingDok[idx] : null;
            const pdfOnly = isPdfOnlyLabel(label);
            return (
              <FileSlot key={idx} label={`${idx + 1}. ${label}`} existingFile={existing} pendingFile={dokFiles[idx]}
                errorMessage={errors[`dok_${idx}`]} pdfOnly={pdfOnly} previewUrl={dokPreview[idx]} previewType={dokFiles[idx]?.type}
                onChange={(e) => handleDokChange(idx, e)}
                onDelete={() => handleDokDelete(idx)} />
            );
          })}
        </Box>
      )}

      {hasFoto && (
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhotoCamera fontSize="small" /> Foto Wajib ({template.fotoWajib.length}) — Maks 2MB
          </Typography>
          {template.fotoWajib.map((label, idx) => {
            const existing = existingFoto[idx] && !fotoDeletes.includes(idx) ? existingFoto[idx] : null;
            return (
              <ImageSlot key={idx} label={`${idx + 1}. ${label}`} existingFile={existing} pendingFile={fotoFiles[idx] || null}
                errorMessage={errors[`foto_${idx}`]} previewUrl={fotoPreview[idx]}
                onChange={(e) => handleFotoChange(idx, e)}
                onDelete={() => handleFotoDelete(idx)} />
            );
          })}
        </Box>
      )}

      {isFisikManual && (
        <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1.5, bgcolor: '#f7f7f7' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Input Langkah Rehab (TFL/Admin) — Anggaran dihitung dari total material
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField label="Tahap Pekerjaan" value={tahapLangkah}
                onChange={(e) => setTahapLangkah(e.target.value)} size="small" fullWidth placeholder="cth: Pembangunan Dinding" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Anggaran Langkah (Rp)" type="number" value={anggaranLangkah}
                onChange={(e) => setAnggaranLangkah(e.target.value)} size="small" fullWidth
                helperText={nominalMaterial > 0 ? `Total material: ${formatRpLocal(nominalMaterial)}` : ''} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Catatan" value={catatanLangkah}
                onChange={(e) => setCatatanLangkah(e.target.value)} size="small" fullWidth multiline rows={2} />
            </Grid>
          </Grid>
        </Box>
      )}

      {isFisikPembangunanTemplate && (
        <Box sx={{ mb: 2 }}>
          <TextField label="Anggaran Langkah (Rp)" type="number" value={anggaranLangkah}
            onChange={(e) => setAnggaranLangkah(e.target.value)} size="small" fullWidth sx={{ maxWidth: 280 }}
            helperText={nominalMaterial > 0 ? `Total material: ${formatRpLocal(nominalMaterial)}` : ''} />
        </Box>
      )}

      {isFisikRtlh && !isFisikManual && (
        <Box sx={{ mb: 2 }}>
          <TextField label="Anggaran Penggunaan (Rp)" type="number" value={anggaranPenggunaan}
            onChange={(e) => setAnggaranPenggunaan(e.target.value)} size="small" fullWidth sx={{ maxWidth: 280 }}
            helperText={nominalMaterial > 0 ? `Total material: ${formatRpLocal(nominalMaterial)}` : ''} />
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button size="small" variant="contained" color={saveButtonColor || (allComplete ? 'success' : 'primary')} disabled={saving} onClick={handleSave}>
          {saving ? 'Menyimpan...' : 'Simpan'}
        </Button>
        <Button size="small" variant="text" onClick={onCancel}>Batal</Button>
      </Stack>
    </Box>
  );
}
